import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../config/firebase';
import { normalizeInterestPeriodAnchorDay } from './interestPaymentsService';

const COLLECTION = 'lenderSettings';

const saveLenderSettingsCallable = httpsCallable(functions, 'saveLenderSettings');

/** Thrown when another lender already claimed this payment email local-part. */
export const PAYMENT_EMAIL_PREFIX_TAKEN = 'PAYMENT_EMAIL_PREFIX_TAKEN';

export function normalizeInterestPaymentEmailLocalPart(raw) {
  if (raw == null || raw === undefined || raw === '') return null;
  const s = String(raw).trim().toLowerCase().replace(/[^a-z0-9.-]/g, '').slice(0, 64);
  return s || null;
}

/** Domaine pour les adresses de paiement (réception Interac). Défaut: rentalyzer.ca. Pour un sous-domaine: VITE_PAYMENT_EMAIL_DOMAIN=payments.rentalyzer.ca */
export const PAYMENT_EMAIL_DOMAIN =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PAYMENT_EMAIL_DOMAIN) || 'rentalyzer.ca';

/**
 * Get lender settings (fund code, payment email prefix, auto-mark payments).
 * @param {string} lenderUid
 * @returns {Promise<{ fundCode?: string, interestPaymentEmailLocalPart?: string, interestPaymentEmail?: string, autoMarkPaymentsEnabled?: boolean } | null>}
 */
export async function getLenderSettings(lenderUid) {
  if (!lenderUid) return null;
  const ref = doc(firestore, COLLECTION, lenderUid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  const localPart = data.interestPaymentEmailLocalPart?.trim();
  const q = Number(data.fundManagerInterestQuotaPct);
  return {
    fundCode: data.fundCode?.trim() || null,
    interestPaymentEmailLocalPart: localPart || null,
    interestPaymentEmail: localPart ? `${localPart}@${PAYMENT_EMAIL_DOMAIN}` : null,
    autoMarkPaymentsEnabled: data.autoMarkPaymentsEnabled === true,
    fundManagerInterestQuotaPct: Number.isFinite(q) && q >= 0 && q <= 100 ? q : 20,
    interestPeriodAnchorDay: normalizeInterestPeriodAnchorDay(data.interestPeriodAnchorDay),
    fundInConfiguration: data.fundInConfiguration === true,
  };
}

/**
 * Subscribe to lender settings.
 * @param {string} lenderUid
 * @param {(data: object | null) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeLenderSettings(lenderUid, callback) {
  if (!lenderUid) {
    callback(null);
    return () => {};
  }
  const ref = doc(firestore, COLLECTION, lenderUid);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      const data = snap.data();
      const localPart = data.interestPaymentEmailLocalPart?.trim();
      const q = Number(data.fundManagerInterestQuotaPct);
      callback({
        fundCode: data.fundCode?.trim() || null,
        interestPaymentEmailLocalPart: localPart || null,
        interestPaymentEmail: localPart ? `${localPart}@${PAYMENT_EMAIL_DOMAIN}` : null,
        autoMarkPaymentsEnabled: data.autoMarkPaymentsEnabled === true,
        fundManagerInterestQuotaPct: Number.isFinite(q) && q >= 0 && q <= 100 ? q : 20,
        interestPeriodAnchorDay: normalizeInterestPeriodAnchorDay(data.interestPeriodAnchorDay),
        fundInConfiguration: data.fundInConfiguration === true,
      });
    },
    () => callback(null)
  );
}

/**
 * Set lender settings. Only the lender (lenderUid === auth.uid) can write.
 * @param {string} lenderUid
 * @param {{ fundCode?: string, interestPaymentEmailLocalPart?: string, autoMarkPaymentsEnabled?: boolean, fundManagerInterestQuotaPct?: number, interestPeriodAnchorDay?: number, fundInConfiguration?: boolean }} data
 */
export async function setLenderSettings(lenderUid, data) {
  if (!lenderUid) throw new Error('lenderUid required');
  const newPart =
    data.interestPaymentEmailLocalPart != null && data.interestPaymentEmailLocalPart !== undefined
      ? normalizeInterestPaymentEmailLocalPart(data.interestPaymentEmailLocalPart)
      : null;
  const payload = {
    fundCode: (data.fundCode != null && data.fundCode !== undefined) ? String(data.fundCode).trim().toUpperCase().slice(0, 20) : null,
    interestPaymentEmailLocalPart: newPart,
    autoMarkPaymentsEnabled: data.autoMarkPaymentsEnabled === true,
  };
  if (data.fundManagerInterestQuotaPct != null && data.fundManagerInterestQuotaPct !== undefined) {
    const q = Number(data.fundManagerInterestQuotaPct);
    if (Number.isFinite(q) && q >= 0 && q <= 100) {
      payload.fundManagerInterestQuotaPct = q;
    }
  }
  if (data.interestPeriodAnchorDay != null && data.interestPeriodAnchorDay !== undefined) {
    payload.interestPeriodAnchorDay = normalizeInterestPeriodAnchorDay(data.interestPeriodAnchorDay);
  }
  if (data.fundInConfiguration != null && data.fundInConfiguration !== undefined) {
    payload.fundInConfiguration = data.fundInConfiguration === true;
  }

  await saveLenderSettingsCallable({
    fundCode: payload.fundCode,
    interestPaymentEmailLocalPart: newPart,
    autoMarkPaymentsEnabled: payload.autoMarkPaymentsEnabled,
    fundManagerInterestQuotaPct: payload.fundManagerInterestQuotaPct,
    interestPeriodAnchorDay: payload.interestPeriodAnchorDay,
    fundInConfiguration: payload.fundInConfiguration,
  });
}
